import { Box, Skeleton, Stack, Typography } from "@mui/material";

const columns = [
  { key: "item", label: "Item", width: "minmax(260px, 2.1fr)" },
  { key: "type", label: "Tipo", width: "minmax(115px, .8fr)" },
  { key: "sector", label: "Sector", width: "minmax(150px, 1fr)" },
  { key: "stock", label: "Stock", width: "minmax(135px, .9fr)" },
  { key: "status", label: "Estado", width: "minmax(115px, .75fr)" },
];

const gridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 1,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

const ACCOUNTING_UNITS = new Set([
  "unit",
  "units",
  "unidad",
  "unidades",
  "package",
  "paquete",
  "paquetes",
]);

const typeLabels = {
  INGREDIENT: "Insumo",
  PREPARED_PRODUCT: "Elaborado",
};

const statusLabels = {
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
};

function normalizeDecimalParts(value) {
  if (value === null || value === undefined || value === "") return null;
  const raw = String(value).trim().replace(",", ".");
  const match = raw.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;

  const sign = match[1] === "-" ? "-" : "";
  const integer = match[2].replace(/^0+(?=\d)/, "") || "0";
  const fraction = (match[3] || "").replace(/0+$/, "");

  return { sign, integer, fraction };
}

function groupThousands(integer) {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function formatDecimalString(value, maximumFractionDigits = 4) {
  const parts = normalizeDecimalParts(value);
  if (!parts) {
    return value === null || value === undefined ? "—" : String(value);
  }

  const fraction = parts.fraction
    .slice(0, maximumFractionDigits)
    .replace(/0+$/, "");
  const decimal = fraction ? `,${fraction}` : "";
  const sign = parts.sign && parts.integer === "0" && !fraction ? "" : parts.sign;
  return `${sign}${groupThousands(parts.integer)}${decimal}`;
}

function isAbsoluteAtLeastThousand(value) {
  const parts = normalizeDecimalParts(value);
  if (!parts) return false;
  const integer = parts.integer.replace(/^0+(?=\d)/, "");
  return integer.length > 4 || (integer.length === 4 && integer >= "1000");
}

function divideDecimalStringByThousand(value) {
  const parts = normalizeDecimalParts(value);
  if (!parts) return value;

  const digits = `${parts.integer}${parts.fraction}`.replace(/^0+(?=\d)/, "");
  const decimalPosition = parts.integer.length - 3;
  let integer;
  let fraction;

  if (decimalPosition > 0) {
    integer = digits.slice(0, decimalPosition) || "0";
    fraction = digits.slice(decimalPosition);
  } else {
    integer = "0";
    fraction = `${"0".repeat(Math.abs(decimalPosition))}${digits}`;
  }

  fraction = fraction.replace(/0+$/, "");
  return `${parts.sign}${integer || "0"}${fraction ? `.${fraction}` : ""}`;
}

function convertToLargerUnitWithoutPrecisionLoss(value) {
  const converted = divideDecimalStringByThousand(value);
  const parts = normalizeDecimalParts(converted);
  if (!parts || parts.fraction.length > 4) return null;
  return converted;
}

function formatStock(quantity, unit) {
  const rawUnit = String(unit || "").trim();
  const normalizedUnit = rawUnit.toLowerCase();

  if (ACCOUNTING_UNITS.has(normalizedUnit)) {
    return `${formatDecimalString(quantity)} ${rawUnit || "unidad"}`;
  }

  if (normalizedUnit === "g" && isAbsoluteAtLeastThousand(quantity)) {
    const converted = convertToLargerUnitWithoutPrecisionLoss(quantity);
    if (converted !== null) return `${formatDecimalString(converted)} kg`;
  }

  if (normalizedUnit === "ml" && isAbsoluteAtLeastThousand(quantity)) {
    const converted = convertToLargerUnitWithoutPrecisionLoss(quantity);
    if (converted !== null) return `${formatDecimalString(converted)} l`;
  }

  return `${formatDecimalString(quantity)} ${rawUnit || ""}`.trim();
}

function formatSector(sector) {
  const values = Array.isArray(sector)
    ? sector
    : typeof sector === "string"
    ? sector.split(",")
    : [];
  const normalized = values
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  const hasKitchen = normalized.some(
    (item) => item.toLocaleLowerCase("es-AR") === "cocina"
  );
  const hasBar = normalized.some(
    (item) => item.toLocaleLowerCase("es-AR") === "barra"
  );

  if (hasKitchen && hasBar) return "Cocina y Barra";
  if (hasKitchen) return "Cocina";
  if (hasBar) return "Barra";
  return normalized.join(" y ") || "—";
}

function TextCell({ children, align = "center" }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: "primary.900",
        fontSize: 13,
        fontWeight: 700,
        textAlign: align,
      }}
    >
      {children}
    </Typography>
  );
}

function StatusPill({ status }) {
  const active = status === "ACTIVE";
  return (
    <Box
      sx={{
        mx: "auto",
        width: 86,
        px: 0.8,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: active
          ? "rgba(33, 150, 83, 0.16)"
          : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: active ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {statusLabels[status] || status || "—"}
      </Typography>
    </Box>
  );
}

function SkeletonRows() {
  return Array.from({ length: 8 }).map((_, rowIndex) => (
    <Box
      key={rowIndex}
      sx={{
        px: 2,
        py: 1.15,
        borderBottom: "1px solid rgba(0,0,0,.06)",
        minHeight: 56,
      }}
    >
      <Box sx={gridSx}>
        {columns.map((column) => (
          <Skeleton
            key={column.key}
            variant="rounded"
            height={22}
            sx={{ mx: column.key === "item" ? 0 : "auto", width: "82%" }}
          />
        ))}
      </Box>
    </Box>
  ));
}

export default function StockInventoryTable({
  rows = [],
  loading = false,
  emptyMessage = "Todavía no hay ítems de stock para mostrar.",
}) {
  const showSkeleton = loading && rows.length === 0;
  const showRows = rows.length > 0;

  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 820,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              overflowY: "hidden",
              scrollbarGutter: "stable",
              px: 2,
              py: 1.45,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "logoblanco",
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: "neutral.50",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            {showSkeleton ? <SkeletonRows /> : null}

            {showRows
              ? rows.map((item) => (
                  <Box
                    key={`${item.type}-${item.id}`}
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderBottom: "1px solid rgba(0,0,0,.06)",
                      minHeight: 58,
                      opacity: loading ? 0.62 : 1,
                      transition: "opacity 160ms ease, background-color 160ms ease",
                      "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
                    }}
                  >
                    <Box sx={gridSx}>
                      <TextCell align="left">{item.itemName || "—"}</TextCell>
                      <TextCell>{typeLabels[item.type] || item.type || "—"}</TextCell>
                      <TextCell>{formatSector(item.sector)}</TextCell>
                      <TextCell>{formatStock(item.quantity, item.unit)}</TextCell>
                      <StatusPill status={item.status} />
                    </Box>
                  </Box>
                ))
              : null}

            {!loading && rows.length === 0 ? (
              <Stack sx={{ p: 3, minHeight: 150, justifyContent: "center" }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 700 }}>
                  {emptyMessage}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
